/* eslint-disable no-use-before-define */
import { replicateRxCollection, RxReplicationState } from 'rxdb/plugins/replication';
import _ from 'lodash';
import { RxError, RxTypeError } from 'rxdb';
import { Subject } from 'rxjs';
import buildPath from '@/tsClient/utilities/buildPath';
import formsBackendClient, { ApiDefinition } from '@/tsClient/servers/noodleQuestionnaires';
import Bluebird from 'bluebird';
import { getNoodleDb } from './rxdb.noodle';
import { RxQuestionnaireTemplateDocType } from './schemas/questionnaireTemplate.schema';
import { RxNoodleDBCollectionsKeys } from './rxdb.types';
import { RxQuestionnaireFieldTemplateDocType } from './schemas/questionnaireFieldTemplate.schema';
import { RX_LOCAL_REPLICATION_INFO } from './rxdb.constants';
import { CompletionPromiseReturn, getPromiseAndResolver, rxdbError, rxdbLog } from './rxdb.tools';

type CheckpointType = {
  id: string;
  updatedAt: string;
};

type ReplicationIdentifier = string;

/**
 * Type representing the response from the replication fetch API.
 * @template T - The document type being replicated
 * @property documents - Array of documents with deletion status and updatedAt timestamp
 * @property checkpoint - The checkpoint of the replication
 */
type PullReplicationFetchResponse<T> = {
  // We need explicitly define the type of the documents array at least with
  // the fields that are REQUIRED for the replication to work.
  documents: (T & { _deleted: boolean })[];
  checkpoint: CheckpointType;
};

interface ReplicationStateInfo {
  status: REPLICATION_INFO_STATUS;
  lastError: Error | null;
  isPausing: boolean;
}

interface ReplicationData {
  addingReplicationPromise: null | CompletionPromiseReturn;
  state: RxReplicationState<unknown, CheckpointType> | null;
  info: ReplicationStateInfo;
  onFinishPull: Subject<void>;
}

interface ReplicationsData {
  [key: ReplicationIdentifier]: ReplicationData | undefined;
}

enum REPLICATION_INFO_STATUS {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  COMPLETED = 'completed',
  ERROR = 'error',
  IDLE = 'idle',
  PAUSED = 'paused',
}

let initialReplicationDate: string | null = null;
let globalCreatorId: string | null = null;

let replicationsData: ReplicationsData = {};
let prioritizedReplications: Set<ReplicationIdentifier> = new Set();
let pausedReplications: Set<ReplicationIdentifier> = new Set();
const QUESTIONNAIRE_FIELD_TEMPLATES_PREFIX = 'questionnaireFieldTemplate';

/**
 * Starts the replication process for a specific creator.
 */
async function startReplications({ creatorId }: { creatorId: string }): Promise<() => void> {
  globalCreatorId = creatorId;

  // It's important to call this function first as it sets the initial replication date
  // Which is then used by the replication process to only fetch documents that have been
  // Deleted since this date (in that way we avoid fetching useless deleted documents).
  await setLocalReplicationInfo();

  rxdbLog('local replication info set');

  await replicateQuestionnaireTemplates({ creatorId });

  rxdbLog('questionnaire templates replicated');

  const path = buildPath<ApiDefinition['/questionnaires/{creatorId}/order-for-replication']['GET']>(
    '/questionnaires/{creatorId}/order-for-replication',
    { creatorId },
  );
  const response = await formsBackendClient.get<{ questionnairesIds: string[]; templatesIds: string[] }>(path);
  const { templatesIds } = response.data;

  rxdbLog('questionnaire field templates replicated');

  await checkUnusedTemplates(templatesIds);

  rxdbLog('unused templates checked');

  return () => {
    cancelAllReplications();
    initialReplicationDate = null;
    globalCreatorId = null;
    replicationsData = {};
    prioritizedReplications = new Set();
    pausedReplications = new Set();
  };
}

/**
 * Sets the local replication info for the database.
 */
async function setLocalReplicationInfo(): Promise<void> {
  try {
    const db = await getNoodleDb();

    const { KEY, INITIAL_DATE_KEY } = RX_LOCAL_REPLICATION_INFO;

    const localReplicationInfo = await db.getLocal(KEY);

    if (localReplicationInfo) {
      initialReplicationDate = localReplicationInfo.get(INITIAL_DATE_KEY);
      return;
    }

    initialReplicationDate = new Date().toISOString();

    await db.insertLocal(KEY, {
      [INITIAL_DATE_KEY]: initialReplicationDate,
    });
  } catch (error) {
    rxdbError('Error setting initial replication date', error);
  }
}

/**
 * Retrieves the questionnaire template replication identifier
 * @returns {ReplicationIdentifier} - The replication identifier.
 */
export function getQuestionnaireTemplateReplicationIdentifier(): ReplicationIdentifier {
  return 'questionnaireTemplate';
}

/**
 * Retrieves the questionnaire field template replication identifier according to the template ID.
 * @param {string} templateId - The ID of the questionnaire template.
 * @returns {ReplicationIdentifier} - The replication identifier.
 */
export function getQuestionnaireFieldTemplateReplicationIdentifier(templateId: string): ReplicationIdentifier {
  return `${QUESTIONNAIRE_FIELD_TEMPLATES_PREFIX}-${templateId}`;
}

async function getReplicationDataByIdentifier(identifier: ReplicationIdentifier): Promise<ReplicationData | null> {
  const replicationData = replicationsData[identifier];
  if (!replicationData) return null;

  // Wait for the replication to be added if it's not already added
  if (replicationData.addingReplicationPromise) {
    await replicationData.addingReplicationPromise.promise;
  }

  return replicationData;
}

/**
 * Replicates questionnaire templates for a specific creator.
 */
function replicateQuestionnaireTemplates({
  creatorId,
}: {
  creatorId: string;
}): Promise<RxReplicationState<RxQuestionnaireTemplateDocType, CheckpointType>> {
  return replicateCollection<RxQuestionnaireTemplateDocType>({
    collection: 'questionnaireTemplate',
    limit: 200,
    path: buildPath('/questionnaire-templates/{creatorId}/pull/questionnaire-templates', { creatorId }),
    replicationIdentifier: getQuestionnaireTemplateReplicationIdentifier(),
  });
}

function createQuestionnaireFieldTemplateReplication(
  templateId: string,
  creatorId: string,
): Promise<RxReplicationState<RxQuestionnaireFieldTemplateDocType, CheckpointType>> {
  return replicateCollection<RxQuestionnaireFieldTemplateDocType>({
    autoStart: false,
    collection: 'questionnaireFieldTemplate',
    limit: 100_000,
    path: buildPath('/questionnaire-templates/{creatorId}/{questionnaireTemplateId}/pull/field-templates', {
      creatorId,
      questionnaireTemplateId: templateId,
    }),
    replicationIdentifier: getQuestionnaireFieldTemplateReplicationIdentifier(templateId),
  });
}

/**
 * Runs the replications for the given identifiers in parallel and in batches.
 * @param {ReplicationIdentifier[]} identifiers - The identifiers of the replications to run.
 * @param {{ concurrency?: number }} options - The options object.
 * @param {number} [options.concurrency=3] - The number of concurrent replications.
 * @returns {Promise<void>} - A promise that resolves when all replications have finished.
 */
async function runReplicationsInBatches(identifiers: ReplicationIdentifier[], { concurrency = 3 }: { concurrency?: number } = {}): Promise<void> {
  await Bluebird.map(
    identifiers,
    async (identifier): Promise<void> => {
      await startOrResyncReplication(identifier, { waitToFinish: true });
    },
    { concurrency },
  );
}

/**
 * Initializes a replication for a specific collection.
 * @param {Object} options - The options object.
 * @param {RxNoodleDBCollectionsKeys} options.collection - The collection to replicate.
 * @param {string} options.path - The path to the collection endpoint.
 * @param {number} [options.limit=100] - The number of documents to pull in each batch.
 * @param {ReplicationIdentifier} [options.replicationIdentifier] - The identifier for the replication.
 * @returns {Promise<RxReplicationState<T, CheckpointType>>} - The replication state.
 */
async function replicateCollection<T>({
  collection,
  path,
  limit,
  replicationIdentifier,
  autoStart = true,
}: {
  collection: RxNoodleDBCollectionsKeys;
  path: string;
  limit: number;
  replicationIdentifier?: ReplicationIdentifier;
  autoStart?: boolean;
}): Promise<RxReplicationState<T, CheckpointType>> {
  if (!initialReplicationDate) {
    throw new Error('Initial replication date not set');
  }

  const db = await getNoodleDb();
  const identifier = replicationIdentifier || collection;
  const existingReplicationData = await getReplicationDataByIdentifier(identifier);

  if (existingReplicationData) {
    rxdbLog(`replicateCollection - replication already exists for identifier: ${identifier}`);
    return existingReplicationData.state as RxReplicationState<T, CheckpointType>;
  }

  const replicationData = createReplicationData(identifier);
  const replicationState = await replicateRxCollection<T, CheckpointType>({
    autoStart,
    collection: db[collection],
    pull: { batchSize: limit, handler: pullHandler },
    replicationIdentifier: identifier,
  });

  addReplicationStateToData(replicationState as RxReplicationState<unknown, CheckpointType>);

  return replicationState;

  /**
   * Executes the request to the backend to fetch the documents to be replicated (a.k.a the documents to be stored in the local database).
   * @param {CheckpointType | undefined} checkpointOrNull - The checkpoint of the replication.
   * @param {number} batchSize - The number of documents to pull in each batch.
   * @returns {Promise<PullReplicationFetchResponse<T>>} - The response from the pull replication.
   */
  async function pullHandler(checkpointOrNull: CheckpointType | undefined, batchSize: number): Promise<PullReplicationFetchResponse<T>> {
    const data = await formsBackendClient.get<PullReplicationFetchResponse<T>>(path, {
      params: {
        deletedFromDate: initialReplicationDate,
        id: checkpointOrNull?.id,
        limit: batchSize,
        updatedAt: checkpointOrNull?.updatedAt,
      },
    });

    // If the number of documents returned is less than the batch size, the replication has finished
    if (data.data.documents.length < batchSize) {
      replicationData.onFinishPull.next();
      handlePrioritizedReplicationsFinished(identifier);
    }

    return data.data;
  }
}

/**
 * Creates a replication data object for a specific identifier.
 * @param {ReplicationIdentifier} identifier - The identifier of the replication.
 * @returns {ReplicationData} - The replication data object.
 */
function createReplicationData(identifier: ReplicationIdentifier): ReplicationData {
  const { IDLE } = REPLICATION_INFO_STATUS;

  const replicationData: ReplicationData = {
    addingReplicationPromise: getPromiseAndResolver(),
    info: {
      isPausing: false,
      lastError: null,
      status: IDLE,
    },
    onFinishPull: new Subject<void>(),
    state: null,
  };

  replicationsData[identifier] = replicationData;

  return replicationData;
}

/**
 * Adds a replication state to the replication data object.
 * Subscribes to the replication state's events.
 * It also resolves the adding replication promise if it exists.
 * @param {RxReplicationState<unknown, CheckpointType>} replicationState - The replication state to add.
 * @returns {ReplicationData | null} - The replication data object or null if the identifier does not exist.
 */
function addReplicationStateToData(replicationState: RxReplicationState<unknown, CheckpointType>): ReplicationData | null {
  const identifier = replicationState.replicationIdentifier;
  const { ACTIVE, CANCELED, COMPLETED, ERROR, IDLE, PAUSED } = REPLICATION_INFO_STATUS;

  if (!replicationsData[identifier]) {
    rxdbError(`addReplicationStateToData - can not add replication state to data as it does not exist: ${identifier}`);
    return null;
  }

  const update = (data: unknown): void => {
    if (!replicationsData[identifier]) return;
    replicationsData[identifier] = _.merge(replicationsData[identifier], data);
  };

  update({ state: replicationState });
  update({ info: { ...replicationsData[identifier]?.info, status: replicationState.wasStarted ? ACTIVE : IDLE } });

  if (replicationsData[identifier]?.addingReplicationPromise) {
    replicationsData[identifier]?.addingReplicationPromise?.resolver();
  }

  update({ addingReplicationPromise: null });

  // Subscribe to error state
  replicationState.error$.subscribe(error => {
    update({ info: { lastError: error, status: ERROR } });

    handleReplicationError(error);
  });

  // Subscribe to active state
  replicationState.active$.subscribe(active => {
    update({ info: { isPausing: false, status: getInfoStatus(active) } });
  });

  // Subscribe to canceled state
  replicationState.canceled$.subscribe(canceled => {
    if (!canceled) return;
    update({ info: { status: CANCELED } });
  });

  return replicationsData[identifier] || null;

  function getInfoStatus(active: boolean): REPLICATION_INFO_STATUS {
    if (active) return ACTIVE;
    if (replicationState.isPaused()) return PAUSED;
    if (replicationState.wasStarted) return COMPLETED;
    return IDLE;
  }

  function handleReplicationError(error: RxError | RxTypeError): void {
    const errors = error?.parameters?.errors;
    const firstErrorMessage = errors?.[0]?.message;

    if (firstErrorMessage?.includes('401')) cancelReplication('authentication error');
    else if (firstErrorMessage?.includes('404')) cancelReplication('404 error');
    else if (firstErrorMessage?.includes('Network Error')) rxdbError(`${replicationState.replicationIdentifier} error due to network error`);
    else rxdbError(`${replicationState.replicationIdentifier} error`, error);

    function cancelReplication(dueReason: string): void {
      rxdbLog(`${replicationState.replicationIdentifier} cancelling due to ${dueReason}.`);
      replicationState.cancel();
    }
  }
}

/**
 * Starts or resyncs a replication by identifier.
 * If there are prioritized replications and this replication is not prioritized, it will be ignored.
 * @param {ReplicationIdentifier} identifier - The identifier of the replication to start or resync.
 * @param {boolean} [options.waitToFinish=false] - Whether to wait for the replication to finish.
 * @returns {Promise<boolean>} - A promise that resolves to true if the replication was started or resynced, false otherwise.
 */
async function startOrResyncReplication(
  identifier: ReplicationIdentifier,
  { waitToFinish = false }: { waitToFinish?: boolean } = {},
): Promise<boolean> {
  let replicationData = await getReplicationDataByIdentifier(identifier);

  if (replicationData?.info?.status === REPLICATION_INFO_STATUS.ACTIVE) return false;

  if (!replicationData?.state) {
    if (!identifier.startsWith(QUESTIONNAIRE_FIELD_TEMPLATES_PREFIX)) return false;

    if (!globalCreatorId) {
      rxdbLog('startOrResyncReplication - globalCreatorId is not set');
      return false;
    }

    await createQuestionnaireFieldTemplateReplication(identifier.split('-')[1], globalCreatorId);
    replicationData = await getReplicationDataByIdentifier(identifier);
  }

  if (!replicationData || !replicationData.state) {
    rxdbLog('startOrResyncReplication - replicationData or replicationData.state is not set for identifier', { identifier });
    return false;
  }

  const { state, info, onFinishPull } = replicationData;

  // Check if there are prioritized replications and this replication is not prioritized
  if (prioritizedReplications.size > 0 && !prioritizedReplications.has(identifier)) {
    pausedReplications.add(identifier);
    return false;
  }

  const { promise: onFinishPromise, resolver: onFinishResolver } = getPromiseAndResolver();

  onFinishPull?.subscribe(onFinishResolver);

  if (info?.status === REPLICATION_INFO_STATUS.COMPLETED) state.reSync();
  else state.start();

  if (waitToFinish) await onFinishPromise;

  return true;
}

/**
 * Resumes all paused replications. This is used when all prioritized replications have been completed.
 */
function resumePausedReplications(): void {
  // We want to handle questionnaire field templates replications separately as we replicate them in batches
  const questionnaireFieldTemplatesIdentifiers = Array.from(pausedReplications).filter(id => id.startsWith(QUESTIONNAIRE_FIELD_TEMPLATES_PREFIX));
  if (questionnaireFieldTemplatesIdentifiers.length > 0) {
    runReplicationsInBatches(questionnaireFieldTemplatesIdentifiers);
  }

  pausedReplications.forEach(id => {
    // Skip questionnaire field templates replications here as they are handled separately
    if (questionnaireFieldTemplatesIdentifiers.includes(id)) return;
    startOrResyncReplication(id);
  });
  pausedReplications.clear();
}

/**
 * Handles the completion of a replication.
 * If the replication was prioritized, it will be removed from the prioritized list.
 * Resumes the paused replications if all prioritized replications have been completed.
 * @param {ReplicationIdentifier} identifier - The identifier of the completed replication.
 */
function handlePrioritizedReplicationsFinished(identifier: ReplicationIdentifier): void {
  if (!prioritizedReplications.has(identifier)) return;

  // Remove the replication from the prioritized list
  prioritizedReplications.delete(identifier);

  // Resume paused replications if all prioritized replications have completed
  if (prioritizedReplications.size === 0) {
    resumePausedReplications();
  }
}

/**
 * Prioritizes a replication by identifier.
 * When this replication is completed, it resumes the paused replications, this is handled by handlePrioritizedReplicationsFinished.
 * @param {ReplicationIdentifier[]} identifiers - The identifiers of the replications to prioritize.
 */
export function prioritizeReplications(identifiers: ReplicationIdentifier[]): void {
  // Add the replications identifiers to the prioritized list first
  identifiers.forEach(identifier => prioritizedReplications.add(identifier));

  // Start or resync the prioritized replications
  identifiers.forEach(identifier => startOrResyncReplication(identifier));
}

async function checkUnusedTemplates(templatesIds: string[]): Promise<void> {
  const db = await getNoodleDb();
  const allTemplates = await db.questionnaireTemplate.find().exec();

  const unusedTemplates = allTemplates.filter(template => !templatesIds.includes(template.id));
  const unusedStaleTemplates = unusedTemplates.filter(template => template.status === 'stale');

  await db.questionnaireFieldTemplate
    .find({
      selector: { questionnaireTemplateId: { $in: unusedStaleTemplates.map(template => template.id) } },
    })
    .remove();
}

function cancelAllReplications(): void {
  Object.values(replicationsData).forEach(replicationData => {
    replicationData?.state?.cancel();
  });
}

export default startReplications;
